import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/clientPublic';
import { EmailOtpType } from '@supabase/supabase-js';
import { useAuth } from '../context/AuthContext';
import { getCurrentUser } from '../services/authService';

const AuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState('');
  const [resendError, setResendError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [manualEmail, setManualEmail] = useState('');
  const { setUser } = useAuth();
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get('token_hash');
  const type = searchParams.get('type');

  const isLinkError =
    error.includes('ungültig oder abgelaufen') ||
    error.includes('Ungültiger oder abgelaufener');

  const getResendEmail = (): string => {
    const emailFromUrl = searchParams.get('email');
    if (emailFromUrl) return emailFromUrl;
    const emailFromStorage = localStorage.getItem('signup_email');
    if (emailFromStorage) return emailFromStorage;
    return '';
  };

  const resolvedEmail = getResendEmail();
  const needsManualEmail = isLinkError && !resolvedEmail;

  const startCooldown = () => {
    setCooldownSeconds(60);
    const interval = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    const emailToUse = resolvedEmail || manualEmail.trim();
    if (!emailToUse) {
      setResendError('Bitte geben Sie Ihre E-Mail-Adresse ein.');
      return;
    }
    setResendLoading(true);
    setResendError('');
    setResendSuccess('');
    try {
      const { error: resendErr } = await supabase.auth.resend({
        type: 'signup',
        email: emailToUse,
      });
      if (resendErr) {
        setResendError('Der Bestätigungslink konnte nicht erneut gesendet werden. Bitte prüfen Sie Ihre E-Mail-Adresse oder versuchen Sie es später erneut.');
      } else {
        setResendSuccess('Wir haben Ihnen einen neuen Bestätigungslink per E-Mail gesendet. Bitte verwenden Sie nur die neueste E-Mail.');
        startCooldown();
      }
    } catch {
      setResendError('Der Bestätigungslink konnte nicht erneut gesendet werden. Bitte prüfen Sie Ihre E-Mail-Adresse oder versuchen Sie es später erneut.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!token || !type) {
      setError('Ungültiger oder abgelaufener Bestätigungslink.');
      return;
    }
    setLoading(true);
    setStatus('Sitzung wird eingerichtet...');
    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: type as EmailOtpType,
      });
      if (sessionError || !session) {
        console.error('Session error:', sessionError);
        const errMsg = sessionError?.message?.toLowerCase() || '';
        if (
          errMsg.includes('expired') ||
          errMsg.includes('invalid') ||
          errMsg.includes('otp') ||
          errMsg.includes('token')
        ) {
          setError('Der Bestätigungslink ist ungültig oder abgelaufen. Bitte registrieren Sie sich erneut oder fordern Sie einen neuen Link an.');
        } else {
          setError('Die Sitzung konnte nicht aufgebaut werden. Bitte versuchen Sie es erneut.');
        }
        return;
      }
      await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      });
      setStatus('Benutzerprofil wird erstellt...');
      let userData = null;
      for (let attempt = 0; attempt < 5; attempt++) {
        userData = await getCurrentUser();
        if (userData) break;
        await new Promise((res) => setTimeout(res, 300));
      }
      if (!userData) {
        setError('Das Benutzerprofil konnte nicht geladen oder erstellt werden. Bitte kontaktieren Sie den Support.');
        return;
      }
      setUser(userData);
      localStorage.setItem('currentUser', JSON.stringify(userData));
      setStatus('Weiterleitung zur Zahlungsseite...');
      setTimeout(() => navigate('/payment-required'), 1000);
    } catch (err) {
      console.error('Auth callback error:', err);
      setError('Ein unerwarteter Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center max-w-md">
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-lg text-gray-700">{status}</p>
            <p className="text-sm text-gray-500 mt-2">
              Bitte warten Sie, während wir Sie zur Zahlungsseite weiterleiten...
            </p>
          </>
        ) : (
          <>
            <h2 className="text-xl font-bold text-gray-800 mb-4">E-Mail bestätigen</h2>
            {error && <p className="text-red-600 text-sm mb-2">{error}</p>}
            <p className="text-gray-700 mb-4">
              Klicken Sie auf den Button unten, um Ihre E-Mail-Adresse zu bestätigen und fortzufahren.
            </p>
            <button
              onClick={handleConfirm}
              disabled={loading}
              className={`bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded font-semibold ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              E-Mail bestätigen
            </button>

            {isLinkError && (
              <div className="mt-6 border-t border-gray-200 pt-4">
                {needsManualEmail && (
                  <div className="mb-3">
                    <input
                      type="email"
                      value={manualEmail}
                      onChange={(e) => setManualEmail(e.target.value)}
                      placeholder="Ihre E-Mail-Adresse"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
                    />
                  </div>
                )}
                {resendSuccess && (
                  <p className="text-green-600 text-sm mb-3">{resendSuccess}</p>
                )}
                {resendError && (
                  <p className="text-red-600 text-sm mb-3">{resendError}</p>
                )}
                {cooldownSeconds > 0 && (
                  <p className="text-gray-500 text-xs mb-2">
                    Bitte warten Sie einen Moment, bevor Sie erneut einen Bestätigungslink anfordern.
                  </p>
                )}
                <button
                  onClick={handleResend}
                  disabled={resendLoading || cooldownSeconds > 0}
                  className={`bg-gray-700 hover:bg-gray-800 text-white px-5 py-2 rounded font-semibold text-sm ${
                    resendLoading || cooldownSeconds > 0 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {cooldownSeconds > 0
                    ? `Neuen Link senden (${cooldownSeconds}s)`
                    : 'Neuen Bestätigungslink senden'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallbackPage;
